import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ThemeConfig } from 'antd';
import { afterHoursTheme, workshopTheme } from 'theme/sbd-theme';

export type ThemeMode = 'after-hours' | 'workshop';

interface ThemeContextValue {
  mode: ThemeMode;
  isDark: boolean;
  antdTheme: ThemeConfig;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'sbd-theme';

function getInitialMode(): ThemeMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'after-hours' ? 'after-hours' : 'workshop';
  } catch {
    return 'workshop';
  }
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);

  useEffect(() => {
    document.documentElement.setAttribute('data-sbd-theme', mode);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* storage unavailable */ }
  }, [mode]);

  const toggleTheme = useCallback(
    () => setMode(prev => (prev === 'after-hours' ? 'workshop' : 'after-hours')),
    []
  );

  const antdTheme = mode === 'after-hours' ? afterHoursTheme : workshopTheme;
  const isDark = mode === 'after-hours';

  return (
    <ThemeContext.Provider value={{ mode, isDark, antdTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
